// src/components/quotes/QuoteDocumentPreview.jsx
//
// The builder's right-hand pane: the real quote document, live.
//
// ── ⚠ THIS IS A VIEWER, NOT AN EDITABLE DOCUMENT, AND THAT IS THE DESIGN ───
// The proposal preview is editable in place — you type into the document and
// drop the cover image onto it — because the SPA owns the proposal templates.
// Quotes are the opposite: `quoteRenderer.js` on the server owns the document,
// deliberately, because that is what stops the PDF's totals drifting from the
// builder's. Rebuilding the quote document as a React component to get
// in-document editing would create two implementations of the same money — the
// bug class `1d8e0ff` fixed inside proposals, at document scale.
//
// So everything edits in the LEFT pane and this pane shows the result. If
// in-document editing is ever wanted, the way to get it is to move the WHOLE
// renderer to the SPA — not to add a second one.
//
// ── ⚠ THE PREVIEW IS A BROWSER; THE PDF IS PRINCE ──────────────────────────
// This iframe renders with Chromium. The PDF renders with Prince, which ignores
// CSS grid and flex `gap`. Anything using those will look correct here and
// collapse in the document. Looking right in this pane is NOT a print proof —
// check a real PDF before trusting a layout change.
import React, { useState, useEffect, useRef } from "react";
import { fetchQuotePreview } from "../../api/quotesApi";

/* A4 at 96dpi. The document is always laid out at its true width so the preview
   matches print; the wrapper scales it down to whatever the pane can give. */
const PAGE_W = 794;
/* One A4 page at the same scale. The starting height, and the floor — a
   document that measures short must not collapse the pane to nothing. */
const PAGE_H = 1123;

const DEBOUNCE_MS = 500;

export default function QuoteDocumentPreview({ quote, ensureMigrated }) {
  const [html, setHtml] = useState("");
  const [state, setState] = useState("idle");   // idle | updating | error
  const [error, setError] = useState("");
  const [scale, setScale] = useState(1);
  const [docHeight, setDocHeight] = useState(PAGE_H);
  const wrapRef = useRef(null);
  const frameRef = useRef(null);

  /* Measure the rendered document so the iframe is exactly as tall as its
     content — no internal scrollbar, no dead white space. Runs on load and
     again whenever a new srcDoc lands.
     ⚠ Wrapped: a cross-origin read throws, and while nothing here should be
     cross-origin, a throw in onLoad would take the whole pane down over a
     cosmetic measurement. */
  const measureDoc = () => {
    try {
      const frame = frameRef.current;
      const doc = frame?.contentDocument;
      if (!doc) return;

      const read = () => {
        const h = doc.documentElement?.scrollHeight || doc.body?.scrollHeight;
        /* +2px of slack. scrollHeight is an integer and the sheet is 210mm —
           793.69px measured — so the true height can land a fraction above what
           this reports, and once the document hides its overflow a short height
           CLIPS rather than showing a bar.

           ⚠ Honest note: measured, this is insurance rather than the fix. The
           document already leaves 31.97px below its last sheet (16px sheet
           margin + 16px body padding), which absorbs the fraction comfortably —
           nothing clipped with the slack or without it. It is kept because it
           costs nothing and that 32px gap is a layout detail nobody is obliged
           to preserve; if it ever shrinks, this is what stops the last line
           disappearing silently. */
        if (h) setDocHeight(Math.max(PAGE_H, h + 2));
      };
      read();

      /* ⚠ RE-MEASURE ONCE THE IMAGES ARE IN. The cover carries the Datatech
         mark and often a customer photo, both as data: URIs — and a data: URI
         still decodes asynchronously. Anything that lands after the first read
         makes the document taller than the height we just set, which is the
         scrollbar back again by another route. The element's own onLoad usually
         waits for subresources, but "usually" is not something to leave a
         visible bar resting on, so both cases are covered explicitly. */
      const win = frame.contentWindow;
      if (win && doc.readyState !== "complete") {
        win.addEventListener("load", read, { once: true });
      }
      Array.from(doc.images || []).forEach((img) => {
        if (!img.complete) img.addEventListener("load", read, { once: true });
      });
    } catch { /* keep the last known height */ }
  };

  /* Refetch when the quote's CONTENT changes, not on every render. Stringifying
     the migrated shape is what the server is being sent anyway, so it is the
     honest fingerprint — and it means a re-render that changed nothing (a focus
     move, a hover) costs no request. */
  const migrated = ensureMigrated ? ensureMigrated(quote) : quote;
  const fingerprint = JSON.stringify(migrated);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setState("updating");

    /* ⚠ DEBOUNCED, and the pane is NEVER blanked while it updates. A preview
       that flashed empty on every keystroke reads as broken, and Craig types
       whole line-item descriptions into this thing. The previous document stays
       on screen under a subtle "updating…" chip until the new one arrives. */
    const t = setTimeout(() => {
      fetchQuotePreview(quote.id, JSON.parse(fingerprint), controller.signal)
        .then((next) => {
          if (cancelled) return;
          setHtml(next);
          setState("idle");
          setError("");
        })
        .catch((err) => {
          if (cancelled || controller.signal.aborted) return;
          setState("error");
          setError(err?.response?.data?.error || err.message || "Preview failed");
        });
    }, DEBOUNCE_MS);

    return () => { cancelled = true; controller.abort(); clearTimeout(t); };
  }, [fingerprint, quote.id]);

  /* Scale the fixed-width document into whatever the pane actually is. Measured
     rather than assumed: the pane's width depends on the window, the 35% column
     and whether a side panel is open. */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const avail = el.clientWidth;
      setScale(avail > 0 ? Math.min(1, avail / PAGE_W) : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div style={{ background: "#b8b8b8", borderRadius: 10, padding: 16,
                  overflow: "auto", maxHeight: "calc(100vh - 190px)", position: "relative" }}>

      {/* Status chip. Sits OVER the document rather than displacing it, so the
          page does not jump every time a render lands. */}
      {state !== "idle" && (
        <div style={{ position: "sticky", top: 0, zIndex: 2, display: "flex", justifyContent: "flex-end",
                      pointerEvents: "none", marginBottom: -10 }}>
          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
                         padding: "3px 9px", borderRadius: 20,
                         background: state === "error" ? "var(--danger)" : "rgba(0,0,0,0.55)",
                         color: "white" }}>
            {state === "error" ? "Preview failed" : "updating…"}
          </span>
        </div>
      )}

      {state === "error" && (
        <div style={{ background: "white", borderRadius: 6, padding: "10px 12px", marginBottom: 12,
                      fontSize: 11, color: "var(--danger)", lineHeight: 1.4 }}>
          {error}
          <div style={{ color: "var(--text3)", marginTop: 4 }}>
            The document below may be out of date. Editing again will retry.
          </div>
        </div>
      )}

      <div ref={wrapRef} style={{ width: "100%" }}>
        {html ? (
          /* ⚠ THE OUTER BOX CARRIES THE SCALED SIZE — BOTH DIMENSIONS.
             A `transform: scale()` does not change layout size, so the inner
             element still reserves its full 794 x docHeight. Without a width
             here `margin: 0 auto` has nothing to centre and the document sits
             hard left in the pane; without a height the grey ground runs on for
             hundreds of empty pixels below it. Both were live bugs. */
          <div style={{ width: PAGE_W * scale, height: docHeight * scale,
                        margin: "0 auto", overflow: "hidden" }}>
            <div style={{ width: PAGE_W, transformOrigin: "top left",
                          transform: `scale(${scale})` }}>
              {/* ⚠ srcDoc, not innerHTML. The server returns a COMPLETE document
                  with its own <style>; innerHTML discards the head and renders it
                  unstyled — the trap that once unstyled the shared reports.
                  `allow-same-origin` WITHOUT `allow-scripts`: no script can run in
                  there, and it is what lets the height below be measured. Same
                  reasoning as the proposal web view. */}
              <iframe
                ref={frameRef}
                title="Quote preview"
                srcDoc={html}
                sandbox="allow-same-origin"
                onLoad={measureDoc}
                /* ⚠ The frame is SIZED TO ITS CONTENT, so it must never scroll.
                   scrolling="no" is the belt to the document's own
                   overflow:hidden brace — deprecated in HTML5 but still the only
                   thing some engines honour on a same-origin frame, and it costs
                   nothing. The horizontal bar was never a separate bug: the
                   vertical one took ~15px, the 793.7px sheet stopped fitting the
                   794px frame, and the horizontal followed. */
                scrolling="no"
                /* ⚠ TRANSPARENT, and no shadow. THIS PANE OWNS THE GREY — the
                   document's own @media screen used to paint #b8b8b8 on its
                   body as well, so two grounds met at the iframe edge and left
                   a visible seam down the page. The sheets inside carry their
                   own white and their own shadow now; anything painted here
                   would sit between the ground and them. */
                style={{ width: PAGE_W, height: docHeight, border: "none",
                         background: "transparent", display: "block" }}
              />
            </div>
          </div>
        ) : (
          <div style={{ background: "white", borderRadius: 6, padding: "40px 20px",
                        textAlign: "center", color: "var(--text3)", fontSize: 12 }}>
            {state === "error" ? "No preview available." : "Building the preview…"}
          </div>
        )}
      </div>
    </div>
  );
}
